import { AgGridReact } from "ag-grid-react";
import type { ColDef, ICellRendererParams } from "ag-grid-community";
import { useDealProvider } from "../../context/DealContext";
import { renderSubTitle } from "../../components/renderComponents";
import { useRef } from "react";
import { dateFormatter, numberFormatter } from "../../utils/numberFormatter";
import { Box } from "@mui/material";
import { CustomButton } from "../../components/common/customButton";
import { createSingleSelectColumn } from "../../utils/columnHelpers";
import { LOAN_COMP_TYPES } from "../../config/loanCompFieldConfig";

import { useReferenceLists } from "../../context/GlobalGetLists";

const ExtractedLoanCompsTable = () => {
  const { loanComps, setLoanComps } = useDealProvider();
  const { countries, sectors } = useReferenceLists();
  const gridRef = useRef<any>(null);

  const handleCheckboxChange = (checked: boolean, loanCompId: number) => {
    const updatedLoanComps = loanComps.map((loanComp: any) => {
      if (loanComp.id === loanCompId) {
        return {
          ...loanComp,
          loanComp: checked ? { id: loanCompId } : null,
        };
      }
      return loanComp;
    });
    setLoanComps(updatedLoanComps as any);
  };

  const columnDefs: ColDef[] = [
    {
      headerName: "",
      field: "included",
      width: 50,
      editable: true,
      cellRenderer: (params: ICellRendererParams) => {
        return (
          <input
            type="checkbox"
            checked={true}
            onChange={(e) =>
              handleCheckboxChange(e.target.checked, params.data?.id)
            }
          />
        );
      },
    },
    {
      field: "loan_type",
      headerName: "Loan Type",
      editable: true,
      width: 160,
      cellEditor: "agSelectCellEditor",
      cellEditorParams: { values: LOAN_COMP_TYPES },
    },
    {
      field: "date",
      headerName: "Date",
      editable: true,
      width: 120,
      valueFormatter: dateFormatter,
    },
    {
      field: "project_name",
      headerName: "Project Name",
      editable: true,
      width: 150,
    },
    { field: "sponsor", headerName: "Sponsor", editable: true, width: 150 },
    createSingleSelectColumn(
      "country",
      "Country",
      countries,
      "nicename",
      150,
      true,
      true,
    ),
    { field: "city", headerName: "City", editable: true, width: 120 },
    createSingleSelectColumn("sector", "Sector", sectors, "sector", 150, true, true),
    {
      field: "lender_type",
      headerName: "Lender Type",
      editable: true,
      width: 150,
    },
    {
      field: "loan_amount_local",
      headerName: "Loan Amount (Local)",
      editable: true,
      width: 150,
      valueFormatter: numberFormatter,
    },
    { field: "ltc_ltv", headerName: "LTC/LTV (%)", editable: true, width: 120 },
    { field: "margin", headerName: "Margin (%)", editable: true, width: 120 },
    { field: "irr", headerName: "IRR (%)", editable: true, width: 100 },
    {
      field: "est_local_irr",
      headerName: "Est. Local IRR (%)",
      editable: true,
      width: 140,
    },
    { field: "comment", headerName: "Comment", editable: true, width: 200 },
  ];

  const onCellValueChanged = (event: any) => {
    const updatedLoanComps = [...loanComps];
    updatedLoanComps[event.rowIndex] = event.data;
    setLoanComps(updatedLoanComps as any);
  };

  const handleAddRow = () => {
    const newRow = {
      loan_type: "",
      date: null,
      project_name: "",
      sponsor: "",
      country: null,
      city: "",
      sector: null,
      lender_type: "",
      loan_amount_local: null,
      ltc_ltv: null,
      margin: null,
      irr: null,
      est_local_irr: null,
      comment: "",
    };
    setLoanComps((prev: any[]) => [...prev, newRow]);
  };

  return (
    <>
      {renderSubTitle("Loan Comps")}

      <div
        className="ag-theme-alpine"
        style={{ height: 300, width: "100%", marginTop: 10 }}
      >
        <AgGridReact
          ref={gridRef}
          rowData={loanComps}
          columnDefs={columnDefs}
          onCellValueChanged={onCellValueChanged}
          singleClickEdit={true}
          stopEditingWhenCellsLoseFocus={true}
          defaultColDef={{ resizable: true, editable: true }}
          rowHeight={30}
        />
      </div>
      <Box display="flex" gap={2} mb={1} mt={2}>
        <CustomButton
          handleButtonPress={handleAddRow}
          isButtonDisabled={false}
          solid={false}
          text="Add Row"
        ></CustomButton>
      </Box>
    </>
  );
};

export default ExtractedLoanCompsTable;
